import { useRef, useSyncExternalStore } from 'react';
import { getRemotionEnvironment, staticFile, useRemotionEnvironment } from 'remotion';
import { ContentVariant, EditorStarterAsset } from '../assets/assets';
import {
  getLocalUrls,
  periodicallyCheckIfLocalUrlIsStillValid,
  useLocalUrls,
} from '../caching/load-to-blob-url';
import {
  isUseVariantUrlEnabled,
  setUseVariantUrl,
  subscribeUseVariantUrl,
} from '@/shared/lib/editor-runtime-options';
import { getPreparedNativeAssetPath } from '@waka/render/native-prepared-assets';

export { setUseVariantUrl };

export const findAssetById = (
  assets: Record<string, EditorStarterAsset>,
  id: string,
): EditorStarterAsset | undefined => {
  return assets[id];
};

/**
 * variant URL 사용 여부 (런타임 토글).
 * 브라우저 콘솔에서: window.__USE_VARIANT_URL = false 로 비활성화 가능.
 *
 * 초기값: localStorage → window 순으로 동기 복원.
 * useEffect 기반 복원(VariantUrlToggle)보다 먼저 실행되어
 * 첫 렌더의 VideoLayer가 올바른 URL을 선택할 수 있도록 함.
 *
 * 기본값 ON: variants(480p) 우선 사용. variants 미보유 콘텐츠는 remoteUrl 폴백.
 */
export const useIsVariantUrlEnabled = (): boolean =>
  useSyncExternalStore(
    subscribeUseVariantUrl,
    isUseVariantUrlEnabled,
    () => true,
  );

const selectLowestMp4Variant = (
  variants: ContentVariant[] | null | undefined,
): { height: number; url: string } | null => {
  if (!isUseVariantUrlEnabled()) return null;
  if (!variants?.length) return null;
  const mp4Variants = variants.filter((v) => v.type === 'MP4');
  if (!mp4Variants.length) return null;
  const lowest = [...mp4Variants].sort((a, b) => a.height - b.height)[0];
  if (!lowest?.url) return null;
  return { height: lowest.height, url: lowest.url };
};

export type ResolvedAssetSource =
  | { kind: 'blob'; url: string }
  | { kind: 'variant'; height: number; url: string }
  | { kind: 'original'; url: string }
  | { kind: 'static'; url: string };

// 단일 소스 오브 트루스: 재생 URL과 분류를 함께 반환
const resolveAssetSource = (
  asset: EditorStarterAsset,
  localUrl: string | undefined,
): ResolvedAssetSource => {
  const preparedPath = getPreparedNativeAssetPath(asset);
  if (preparedPath) return { kind: 'static', url: staticFile(preparedPath) };

  // 우선순위: localUrl(blob) > variants(프리뷰 전용) > remoteUrl > staticPath
  if (localUrl) return { kind: 'blob', url: localUrl };

  // 프리뷰 전용: variants에서 가장 낮은 해상도 MP4 선택
  if (!getRemotionEnvironment().isRendering && asset.type === 'video') {
    const v = selectLowestMp4Variant(asset.variants);
    if (v) return { kind: 'variant', height: v.height, url: v.url };
  }

  if (asset.remoteUrl) return { kind: 'original', url: asset.remoteUrl };
  if (asset.staticPath) {
    return { kind: 'static', url: staticFile(asset.staticPath) };
  }

  throw new Error(`Asset ${asset.id} has neither remote nor local URL`);
};

const resolveAssetUrl = (
  asset: EditorStarterAsset,
  localUrl: string | undefined,
): string => resolveAssetSource(asset, localUrl).url;

// useStablePreferredLocalUrl의 generic media 런타임 전환 경로에서 사용
const selectVariantUrl = (
  variants: ContentVariant[] | null | undefined,
): string | null => selectLowestMp4Variant(variants)?.url ?? null;

// `none`은 resolve 실패(URL 없음) 자산을 표시하는 에러 대체값.
// 정상 경로에서는 발생하지 않으며, getAssetSourceKind의 catch 블록에서만 반환됨.
export type AssetSourceKind =
  | { kind: 'blob' }
  | { kind: 'variant'; height: number }
  | { kind: 'original' }
  | { kind: 'static' }
  | { kind: 'none' };

export const getAssetSourceKind = (
  asset: EditorStarterAsset,
  localUrl: string | undefined,
): AssetSourceKind => {
  try {
    const src = resolveAssetSource(asset, localUrl);
    return src.kind === 'variant'
      ? { kind: 'variant', height: src.height }
      : { kind: src.kind };
  } catch {
    return { kind: 'none' };
  }
};

export const usePreferredLocalUrl = (asset: EditorStarterAsset) => {
  const localUrls = useLocalUrls();
  const localUrl = localUrls[asset.id];

  if (localUrl && !getPreparedNativeAssetPath(asset)) {
    periodicallyCheckIfLocalUrlIsStillValid(localUrl, asset);
  }

  return resolveAssetUrl(asset, localUrl);
};

export const keepMountedAssetSourceUrl = ({
  currentUrl,
  asset,
  localUrl,
}: {
  currentUrl: string;
  asset: EditorStarterAsset;
  localUrl: string | undefined;
}): string => {
  const preparedPath = getPreparedNativeAssetPath(asset);
  if (preparedPath) return staticFile(preparedPath);

  // 이미 디코딩 중인 remote/variant/static 소스를 blob으로 교체하면 video element가
  // 다시 로드되어 경계에서 빈 프레임이 생긴다. 새 소스 선택은 다음 마운트에 맡긴다.
  if (!currentUrl.startsWith('blob:')) return currentUrl;

  // 브라우저가 blob URL을 폐기해 재생성한 경우에만 같은 종류의 소스로 교체한다.
  if (localUrl?.startsWith('blob:')) return localUrl;

  // asset은 호출 계약상 현재 아이템의 자산임을 명확히 하고, URL 재생성 중에는
  // 기존 blob을 유지해 remote로 역전환하지 않는다.
  void asset;
  return currentUrl;
};

// generic media는 기존 runtime remote→blob/variant 전환 정책을 유지한다.
// 경계 아바타만 keepMountedRemoteSource로 현재 디코딩 소스를 고정한다.
export const useStablePreferredLocalUrl = (
  asset: EditorStarterAsset,
  {
    keepMountedRemoteSource = false,
  }: { keepMountedRemoteSource?: boolean } = {},
) => {
  const stableSrcRef = useRef<string | null>(null);
  const environment = useRemotionEnvironment();
  const preparedPath = getPreparedNativeAssetPath(asset);
  if (preparedPath) return staticFile(preparedPath);
  if(environment.isClientSideRendering) return asset.remoteUrl || (asset.staticPath ? staticFile(asset.staticPath) : '');

  if (stableSrcRef.current === null) {
    const localUrls = getLocalUrls();
    const localUrl = localUrls[asset.id];
    stableSrcRef.current = resolveAssetUrl(asset, localUrl);
  }

  if (
    !keepMountedRemoteSource &&
    stableSrcRef.current &&
    !stableSrcRef.current.startsWith('blob:')
  ) {
    const localUrls = getLocalUrls();
    const localUrl = localUrls[asset.id];
    if (localUrl) {
      stableSrcRef.current = localUrl;
    } else if (
      !getRemotionEnvironment().isRendering &&
      asset.type === 'video'
    ) {
      const variantUrl = selectVariantUrl(asset.variants);
      if (variantUrl && stableSrcRef.current !== variantUrl) {
        stableSrcRef.current = variantUrl;
      }
    }
  }

  // blob URL이면 유효성 주기 체크 + 재생성된 경우 갱신
  if (stableSrcRef.current?.startsWith('blob:')) {
    const localUrls = getLocalUrls();
    const currentLocal = localUrls[asset.id];
    stableSrcRef.current = keepMountedAssetSourceUrl({
      currentUrl: stableSrcRef.current,
      asset,
      localUrl: currentLocal,
    });
    periodicallyCheckIfLocalUrlIsStillValid(stableSrcRef.current, asset);
  }

  return stableSrcRef.current;
};
